<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { watch, computed, ref, nextTick } from "vue"
import { useI18n } from "vue-i18n"
import {
    useOwnerForm,
    type OwnerOutput,
    type OwnerUserData,
    type UserType,
    type OwnerType,
} from "@core/composables"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            owner: {
                helper: "Qui est le titulaire de cette source ?",
                infoParticulierExist:
                    "Cet utilisateur existe déjà en tant que particulier.",
                warningParticulierNotAllowed:
                    "Le titulaire doit être une personne morale (professionnel). Veuillez renseigner quelqu'un d'autre.",
                warningProfessionalNotAllowed:
                    "Le titulaire doit être un particulier.",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
interface Props {
    /**
     * Valeur du titulaire (v-model)
     */
    modelValue?: OwnerOutput | null
    /**
     * Titre affiché au-dessus du sélecteur Mon entreprise / Client externe (défaut : libellé i18n « une source »)
     */
    helperTitle?: string
    /**
     * Type d'utilisateur accepté (null = les deux types acceptés)
     */
    allowedUserType?: UserType | null
    /**
     * Message d'avertissement à afficher en cas de non-conformité du type
     */
    warningParticulierNotAllowed?: string
    /**
     * Message d'avertissement à afficher en cas de non-conformité du type
     */
    warningProfessionalNotAllowed?: string
}

interface Emits {
    /**
     * Émis lors du changement de valeur (null = formulaire réinitialisé)
     */
    (e: "update:modelValue", value: OwnerOutput | null): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    modelValue: null,
    helperTitle: undefined,
    allowedUserType: null,
    warningParticulierNotAllowed: "",
    warningProfessionalNotAllowed: "",
})

const emit = defineEmits<Emits>()

// ================================
// COMPOSABLES
// ================================
const {
    // État
    ownerType,
    user,
    userType,
    selectedCompany,
    foundUser,
    hasSearchedUser,
    isSearchingByEmail,
    filteredSuggestions,
    isSearchingSuggestions,

    // Formulaire entreprise
    companyForm,
    companyTypeOptions,
    legalFormOptions,
    companyIdErrorMessage,

    // Computed
    getUserHasCompanies,
    getUserCompanies,
    getIsUserBasicValid,
    getShowCompanyForm,
    getShowUserTypeSelector,
    getIsValid,
    getOutput,
    getAllowedUserTypeOptions,

    // Actions
    searchUser,
    filterSuggestions,
    onSelectSuggestion,
    resetSearchState,
    setInitialValue,
    onCompanyTypeChange,
} = useOwnerForm({
    initialValue: props.modelValue || undefined,
    allowedUserType: props.allowedUserType || undefined,
})

// ================================
// DONNÉES CALCULÉES
// ================================

/**
 * Indique si l'utilisateur trouvé est un particulier (sans entreprise)
 */
const getIsParticularUserFound = computed(() => {
    return foundUser.value && !getUserHasCompanies.value
})

/**
 * Indique si le type d'utilisateur trouvé ne correspond pas au type attendu
 */
const getShowTypeMismatchWarning = computed(() => {
    if (!props.allowedUserType || !hasSearchedUser.value || !foundUser.value) {
        return false
    }

    const foundUserType: UserType = getUserHasCompanies.value
        ? "professional"
        : "particular"
    return foundUserType !== props.allowedUserType
})

/**
 * Message d'avertissement à afficher en cas de non-conformité du type
 */
const getTypeMismatchMessage = computed(() => {
    if (!props.allowedUserType) return ""

    if (props.allowedUserType === "professional") {
        return (
            props.warningParticulierNotAllowed ||
            t("owner.warningParticulierNotAllowed")
        )
    } else {
        return (
            props.warningProfessionalNotAllowed ||
            t("owner.warningProfessionalNotAllowed")
        )
    }
})

// ================================
// WATCHERS
// ================================

/**
 * Indique qu'une mise à jour de modelValue provient de notre emit (évite la boucle récursive)
 */
const isEmitFromOutput = ref(false)

/**
 * État précédent de la validité du formulaire
 */
const previousIsValid = ref(false)

/**
 * Synchronise les valeurs initiales avec le formulaire
 * Ignore les mises à jour qui proviennent de notre propre emit (v-model)
 */
watch(
    () => props.modelValue,
    (newValue) => {
        if (isEmitFromOutput.value) return
        if (newValue) {
            setInitialValue(newValue)
        }
    },
    { deep: true }
)

/**
 * Émet null au passage Mon entreprise → Client externe (formulaire réinitialisé)
 */
watch(ownerType, (newType, oldType) => {
    if (oldType === "self" && newType === "client") {
        isEmitFromOutput.value = true
        emit("update:modelValue", null)
        nextTick(() => {
            isEmitFromOutput.value = false
        })
        return
    }
    // Au passage à "Mon entreprise", émettre après que setOwnerFromCurrentUser ait rempli user/foundUser
    if (newType === "self") {
        nextTick(() => {
            if (getIsValid.value) {
                isEmitFromOutput.value = true
                emit("update:modelValue", getOutput.value)
                nextTick(() => {
                    isEmitFromOutput.value = false
                })
            }
        })
    }
})

/**
 * Émet les changements du formulaire uniquement si le formulaire est valide
 */
watch(
    getOutput,
    (newOutput) => {
        // Ne pas émettre si le formulaire n'est pas valide
        if (!getIsValid.value) return

        isEmitFromOutput.value = true
        emit("update:modelValue", newOutput)
        nextTick(() => {
            isEmitFromOutput.value = false
        })
    },
    { deep: true }
)

/**
 * Émet aussi quand le formulaire devient valide (pour déclencher la mise à jour
 * quand hasSearchedUser passe à true sans que getOutput change)
 * Émet null quand le formulaire devient invalide pour désactiver le bouton
 */
watch(getIsValid, (isValid) => {
    if (isEmitFromOutput.value) return

    const wasValid = previousIsValid.value
    previousIsValid.value = isValid

    // Si le formulaire devient invalide, émettre null au tick suivant pour ne pas bloquer,
    // dans le même tick, une transition invalide → valide (ex. passage « Mon entreprise » :
    // reset puis remplissage utilisateur connecté).
    if (!isValid) {
        nextTick(() => {
            if (isEmitFromOutput.value) return
            if (!getIsValid.value) {
                isEmitFromOutput.value = true
                emit("update:modelValue", null)
                nextTick(() => {
                    isEmitFromOutput.value = false
                })
            }
        })
        return
    }

    // Si le formulaire devient valide, émettre la valeur
    if (isValid && !wasValid) {
        isEmitFromOutput.value = true
        emit("update:modelValue", getOutput.value)
        nextTick(() => {
            isEmitFromOutput.value = false
        })
    }
})

// ================================
// INTERACTIONS
// ================================

/**
 * Gère le changement de type de titulaire
 * @param value - Nouveau type
 */
function onChangeOwnerType(value: OwnerType): void {
    ownerType.value = value
}

/**
 * Gère la mise à jour des données utilisateur
 * @param value - Nouvelles données
 */
function onUpdateUser(value: OwnerUserData): void {
    user.value = value
}

/**
 * Gère la recherche de suggestions
 * @param query - Requête de recherche
 */
function onCompleteSuggestions(query: string): void {
    filterSuggestions(query)
}

/**
 * Gère le changement de l'email (réinitialise la recherche)
 */
function onEmailChange(): void {
    resetSearchState()
}

/**
 * Gère le clic sur rechercher
 */
function onClickSearch(): void {
    searchUser()
}

/**
 * Gère le changement de type d'utilisateur
 * @param value - Nouveau type
 */
function onChangeUserType(value: UserType): void {
    userType.value = value
}

defineExpose({
    getIsValid,
})
</script>

<template>
    <div class="source-form-owner flex flex-col gap-6 w-full">
        <!-- ================================ -->
        <!-- TITRE -->
        <!-- ================================ -->
        <h2 class="text-lg font-semibold text-gray-700 text-center">
            {{ props.helperTitle ?? t("owner.helper") }}
        </h2>

        <!-- ================================ -->
        <!-- SÉLECTEUR TYPE TITULAIRE -->
        <!-- ================================ -->
        <OwnerTypeSelector
            :model-value="ownerType"
            @update:model-value="onChangeOwnerType"
        />

        <!-- ================================ -->
        <!-- FORMULAIRE CLIENT EXTERNE -->
        <!-- ================================ -->
        <div v-if="ownerType === 'client'" class="flex flex-col gap-6">
            <!-- ================================ -->
            <!-- FORMULAIRE UTILISATEUR -->
            <!-- ================================ -->
            <OwnerUserForm
                :model-value="user"
                :suggestions="filteredSuggestions"
                :filtered-suggestions="filteredSuggestions"
                :is-searching-suggestions="isSearchingSuggestions"
                :is-searching-by-email="isSearchingByEmail"
                :has-searched="hasSearchedUser"
                :show-search-button="true"
                :disable-search-button="!getIsUserBasicValid"
                @update:model-value="onUpdateUser"
                @complete="onCompleteSuggestions"
                @select="onSelectSuggestion"
                @email-change="onEmailChange"
                @search="onClickSearch"
            />

            <!-- ================================ -->
            <!-- MESSAGE UTILISATEUR PARTICULIER TROUVÉ -->
            <!-- ================================ -->
            <BaseMessage v-if="getIsParticularUserFound" severity="info">
                <p class="text-sm">
                    {{ t("owner.infoParticulierExist") }}
                </p>
            </BaseMessage>

            <!-- ================================ -->
            <!-- MESSAGE D'AVERTISSEMENT TYPE NON CONFORME -->
            <!-- ================================ -->
            <BaseMessage v-if="getShowTypeMismatchWarning" severity="warn">
                <p class="text-sm">{{ getTypeMismatchMessage }}</p>
            </BaseMessage>

            <!-- ================================ -->
            <!-- SÉLECTEUR TYPE UTILISATEUR -->
            <!-- ================================ -->
            <OwnerUserTypeSelector
                v-if="getShowUserTypeSelector"
                :model-value="userType"
                :allowed-types="getAllowedUserTypeOptions"
                @update:model-value="onChangeUserType"
            />

            <!-- ================================ -->
            <!-- FORMULAIRE ENTREPRISE -->
            <!-- ================================ -->
            <OwnerCompanyForm
                v-if="getShowCompanyForm"
                :model-value="selectedCompany"
                :companies="getUserCompanies"
                :username="foundUser?.username || user.username"
                :company-form="companyForm"
                :company-type-options="companyTypeOptions"
                :legal-form-options="legalFormOptions"
                :company-id-error="companyIdErrorMessage || ''"
                @update:model-value="selectedCompany = $event"
                @update:company-form="companyForm = $event"
                @type-change="onCompanyTypeChange"
            />
        </div>

        <slot name="footer" />
    </div>
</template>
